import { isAfter, isToday } from 'date-fns';

/**
 * Check if a date/time is today and not yet passed (now or in the future today)
 *
 * Returns true only if the date is today AND the time has not yet passed.
 * Returns false for past days, future days, or past times today.
 *
 * @param date - Date to check (Date object or ISO string)
 * @param referenceDate - Reference date to compare against (defaults to now)
 *
 * @returns true if the date is today and the time is now or after the reference time
 *
 * @example
 * ```ts
 * // Current time: 2025-01-15 14:00
 *
 * // Today at 16:00 (future time today)
 * isTodayOrLaterToday('2025-01-15T16:00:00.000Z')
 * // => true
 *
 * // Today at 14:00 (current time)
 * isTodayOrLaterToday('2025-01-15T14:00:00.000Z')
 * // => true
 *
 * // Today at 12:00 (past time today)
 * isTodayOrLaterToday('2025-01-15T12:00:00.000Z')
 * // => false
 *
 * // Yesterday at 16:00 (different day)
 * isTodayOrLaterToday('2025-01-14T16:00:00.000Z')
 * // => false
 *
 * // Tomorrow at 12:00 (different day)
 * isTodayOrLaterToday('2025-01-16T12:00:00.000Z')
 * // => false
 * ```
 */
export const isTodayOrLaterToday = (date: Date | string, referenceDate: Date = new Date()): boolean => {
  const targetDate = typeof date === 'string' ? new Date(date) : date;

  return isToday(targetDate) && !isAfter(referenceDate, targetDate);
};
